import { WalletError } from '@cef-ai/wallet-api-client';
import { walletErrorMessage } from '~/wallet';

/**
 * The two conflict conditions, as two strings, because one string cannot be
 * true for both.
 *
 * `PATCH /v1/accounts/me` answering `409 email-conflict` establishes ONE fact:
 * the address is already in use. It does not say by whom — and the likeliest
 * conflicting address in this flow is the reader's OWN, in a second tab whose
 * `identity.account` predates a bind made in the first. Whose it is can only
 * be settled by `accounts.me`, which is JWT-authed and therefore speaks about
 * THIS account and no other.
 *
 * So ownership is a claim a caller must EARN, and the two outcomes get
 * separate strings rather than sharing one:
 *
 * - `EMAIL_CONFLICT_ANOTHER_ACCOUNT` — `accounts.me` answered and the address
 *   is demonstrably not on this account. Only `EmailBindFlow.resolveConflict`
 *   may use it, and only on that branch.
 * - `EMAIL_CONFLICT_OWNER_UNKNOWN` — the conflict is real but the lookup did
 *   not answer (network, 401, anything). It states the condition, offers two
 *   things the reader can actually do, and asserts nothing about the owner.
 *
 * This split is the shape fix, not the instance fix. The first version routed
 * both conditions through the ownership-asserting string, so the false
 * statement removed from the pre-empt simply reappeared down the error path of
 * the code that replaced it — and a test pinned it there as intended. Making
 * the SAFE string the default of `emailBindErrorMessage` means a future
 * condition that reaches this code without having checked ownership gets a
 * true sentence by default rather than a false one.
 */
export const EMAIL_CONFLICT_ANOTHER_ACCOUNT =
  'That address already belongs to another account. Enter a different one to use here.';

export const EMAIL_CONFLICT_OWNER_UNKNOWN =
  "That address is already in use, and we couldn't check whether it is already on your own account. Try again in a moment, or enter a different address.";

/** Which half of the Settings binding flow produced the failure. */
export type BindStep = 'email' | 'otp';

/**
 * Copy for the Settings "add an email" flow, branched on `WalletError.code`
 * and never on a message string — same rule as `registerErrorMessage`, and
 * the same reason: `rate-limited` and `otp-invalid` need different remedies,
 * and the code is the only stable thing that separates them.
 *
 * It is a SEPARATE table from the registration one because the remedies
 * differ, not because the codes do. `email-conflict` in registration means
 * "you already have a wallet — sign in to it"; here the reader is already
 * signed in to a DIFFERENT account, so the only thing they can do on this
 * screen is type another address. Reusing registration's wording would send
 * them somewhere that solves nothing.
 *
 * Every sentence below names only what is on screen right now: the address
 * box, the "Send a new code" button, or waiting. Nothing here refers to
 * recovery, to a second device, or to support — none of which exist. And no
 * sentence asserts a fact the caller has not established: see the two
 * `EMAIL_CONFLICT_*` strings above.
 */
export function emailBindErrorMessage(e: unknown, step: BindStep): string {
  if (e instanceof WalletError) {
    switch (e.code) {
      case 'rate-limited':
        return step === 'email'
          ? 'Too many codes have been requested for that address. Wait a few minutes, then try again.'
          : 'Too many attempts. Wait a few minutes, then request a new code.';
      case 'otp-invalid':
        return "That code didn't match. Check the six digits, or request a new code.";
      case 'email-conflict':
        // The SAFE of the two conflict messages is the default, deliberately.
        // A bare `email-conflict` says the address is in use; it does not say
        // by WHOM, and nothing on this path has established that. Naming an
        // owner requires the verified branch below, which a caller has to ask
        // for by name.
        return EMAIL_CONFLICT_OWNER_UNKNOWN;
      case 'email-unverified':
        // The token is single-use; `PATCH /v1/accounts/me` rejects a spent
        // one. The flow returns to the address step, so the copy has to say
        // why the reader is back there.
        return 'That confirmation code has already been used. Enter your address again and we will send a new one.';
      case 'validation':
        return step === 'email'
          ? "That doesn't look like an email address. Check it and try again."
          : 'That code looks incomplete — it is six digits.';
      case 'unauthorized':
      case 'token-expired':
        // This flow is the one place in the email machinery that needs a
        // session (`PATCH /v1/accounts/me` is JWT-authed), so unlike
        // registration it can genuinely hit an expired one.
        return 'Your session expired before we could save the address. Reload the page and try again.';
      default:
        return walletErrorMessage(e);
    }
  }
  return walletErrorMessage(e);
}
